import { DraftEmailAttachment } from "client/Api/Email/respondToEmail";
import { AlertManager, useAlerts } from "client/Hooks";
import { MailMessageLocationQueryModel } from "client/Api/Email/common";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { newGuid, Guid } from "client_legacy/ndaw/guid";
import { useCallback, useMemo } from "react";
import { InsertEmbeddedImageResponseModel, insertEmbedImageToExchange } from "client/Api/Email/email";
import {
	AttachmentItem,
	AttachmentKind,
	AttachmentStatus,
	convertToAttachmentItem,
	EnqueueAttachmentUpload,
	isUploadedInlineAttachment,
	UploadedInlineAttachment
} from "./common";

export type UploadInlineImageOptions = {
	draft: MailMessageLocationQueryModel;
	sizeLimit: number;
	enqueueUpload: EnqueueAttachmentUpload;
	onAddAttachment(attachment: AttachmentItem): void;
	onAttachmentUploadFinish(uid: string, model: DraftEmailAttachment): void;
	onRemoveAttachment(uid: string): void;
};

export type UploadInlineImageHandlers = {
	uploadInlineImage(file: File, totalSize: number): Promise<UploadedInlineAttachment | undefined>;
};

export function useUploadInlineImage(options: UploadInlineImageOptions): UploadInlineImageHandlers {
	const {
		draft,
		sizeLimit,
		enqueueUpload,
		onAddAttachment,
		onAttachmentUploadFinish,
		onRemoveAttachment
	} = options;

	const alertManager: AlertManager = useAlerts();

	const uploadInlineImage = useCallback(
		async (
			file: File,
			totalSize: number
		): Promise<UploadedInlineAttachment | undefined> => {
			const uid: Guid = newGuid();

			if (totalSize + file.size > sizeLimit) {
				alertManager.showError(localize("_OneOrMoreAttachmentsAreToBigToUpload"));
				return undefined;
			}

			onAddAttachment(
				{
					status: AttachmentStatus.Uploading,
					kind: AttachmentKind.Inline,
					name: file.name,
					size: file.size,
					uid
				}
			);

			try {
				const response: InsertEmbeddedImageResponseModel = await enqueueUpload(
					() => insertEmbedImageToExchange(
						draft,
						file
					)
				);

				onAttachmentUploadFinish(uid, response.attachment);

				const uploadedAttachment: AttachmentItem = convertToAttachmentItem(
					response.attachment,
					uid
				);

				if (!isUploadedInlineAttachment(uploadedAttachment)) {
					throw new Error("Attachment is not inline");
				}

				return uploadedAttachment;
			} catch(e) {
				if (isCancelledError(e)) {
					return undefined;
				}

				onRemoveAttachment(uid);

				alertManager.showError(localize("_UploadInsertImageError"));
				return undefined;
			}
		},
		[
			alertManager,
			draft,
			enqueueUpload,
			onAddAttachment,
			onAttachmentUploadFinish,
			onRemoveAttachment,
			sizeLimit
		]
	);

	return useMemo(
		(): UploadInlineImageHandlers => ({
			uploadInlineImage
		}),
		[uploadInlineImage]
	);
}